import { useCallback, useState, useMemo, type ReactNode } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  type ColumnDef,
  type Table as ReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  ItemSlug,
  PostSlug,
  IntegrationSlug,
  DateDisplay,
  PhaseChip,
  useListItemVersions,
  useListPosts,
  useListIntegrations,
  useListActions,
  useListUsers,
  useInvalidateLists,
} from "./components.js";
import { Actor } from "./actor.js";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import {
  getContextualActionsForItem,
  getContextualActionsForPost,
  getContextualActionsForIntegration,
  type ItemVersion,
  type Post,
  type Integration,
  type ActionLogEntry,
  type OrgUser,
} from "scantling-common";
import { ContextualActionsWidget } from "./contextual_actions_widget.js";

function getItemColumns(
  onItemActionSuccess?: () => void,
): ColumnDef<ItemVersion>[] {
  const columns: ColumnDef<ItemVersion>[] = [
    {
      accessorKey: "slug",
      header: "Slug",
      cell: ({ row }) => (
        <ItemSlug
          slug={row.original.slug}
          version={row.original.version}
          item={row.original}
        />
      ),
    },
    {
      accessorKey: "niceName",
      header: "Name",
      cell: ({ row }) => (
        <span className={row.original.niceName ? "" : "text-muted-foreground"}>
          {row.original.niceName || "—"}
        </span>
      ),
    },
    {
      accessorKey: "phase",
      header: "Phase",
      cell: ({ row }) => <PhaseChip phase={row.original.phase} />,
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ row }) => <DateDisplay value={row.original.createdAt} />,
    },
  ];

  if (onItemActionSuccess) {
    columns.push({
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const actions = getContextualActionsForItem(row.original);
        return (
          <ContextualActionsWidget
            actions={actions}
            onActionSuccess={onItemActionSuccess}
          />
        );
      },
    });
  }

  return columns;
}

function getPostColumns(onPostActionSuccess?: () => void): ColumnDef<Post>[] {
  const columns: ColumnDef<Post>[] = [
    {
      accessorKey: "slug",
      header: "Post",
      cell: ({ row }) => (
        <PostSlug slug={row.original.slug} post={row.original} />
      ),
    },
    {
      accessorKey: "niceName",
      header: "Name",
      cell: ({ row }) => (
        <span className={row.original.niceName ? "" : "text-muted-foreground"}>
          {row.original.niceName || "—"}
        </span>
      ),
    },
    {
      accessorKey: "itemSlug",
      header: "Item",
      cell: ({ row }) => (
        <ItemSlug
          slug={row.original.itemSlug}
          version={row.original.itemVersion}
        />
      ),
    },
    { accessorKey: "channelSlug", header: "Channel" },
    {
      accessorKey: "phase",
      header: "Phase",
      cell: ({ row }) => <PhaseChip phase={row.original.phase} />,
    },
    {
      accessorKey: "scheduledAt",
      header: "Scheduled",
      cell: ({ row }) => (
        <DateDisplay
          value={row.original.scheduledAt}
          fallback={<span className="text-muted-foreground">—</span>}
        />
      ),
    },
  ];

  if (onPostActionSuccess) {
    columns.push({
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const actions = getContextualActionsForPost(row.original);
        return (
          <ContextualActionsWidget
            actions={actions}
            onActionSuccess={onPostActionSuccess}
          />
        );
      },
    });
  }

  return columns;
}

function getIntegrationsColumns(
  onIntegrationActionSuccess?: () => void,
): ColumnDef<Integration>[] {
  const columns: ColumnDef<Integration>[] = [
    {
      accessorKey: "slug",
      header: "Slug",
      cell: ({ row }) => (
        <IntegrationSlug slug={row.original.slug} integration={row.original} />
      ),
    },
    {
      accessorKey: "niceName",
      header: "Name",
      cell: ({ row }) => (
        <span className={row.original.niceName ? "" : "text-muted-foreground"}>
          {row.original.niceName || "—"}
        </span>
      ),
    },
    {
      accessorKey: "type",
      header: "Type",
    },
    {
      accessorKey: "enabled",
      header: "Enabled",
      cell: ({ row }) => (row.original.enabled ? "Yes" : "No"),
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ row }) => <DateDisplay value={row.original.createdAt} />,
    },
  ];

  if (onIntegrationActionSuccess) {
    columns.push({
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const actions = getContextualActionsForIntegration(row.original);
        return (
          <ContextualActionsWidget
            actions={actions}
            onActionSuccess={onIntegrationActionSuccess}
          />
        );
      },
    });
  }

  return columns;
}

interface DataTableProps<T> {
  table: ReactTable<T>;
  globalFilter: string;
  setGlobalFilter: (v: string) => void;
  columnCount: number;
  searchPlaceholder: string;
  emptyMessage: string;
  permanentFilter?: string;
}

function DataTable<T>({
  table,
  globalFilter,
  setGlobalFilter,
  columnCount,
  searchPlaceholder,
  emptyMessage,
  permanentFilter,
}: DataTableProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <Input
          placeholder={searchPlaceholder}
          value={globalFilter}
          onChange={e => setGlobalFilter(e.target.value)}
        />
        {permanentFilter && (
          <span className="text-xs text-muted-foreground text-right">
            {permanentFilter}
          </span>
        )}
      </div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(group => (
            <TableRow key={group.id}>
              {group.headers.map(header => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columnCount}
                className="text-center text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface ItemTableProps {
  search?: string;
  emptyState?: ReactNode;
}

export function ItemTable({ search = "", emptyState }: ItemTableProps) {
  const items = useListItemVersions(search);
  const invalidate = useInvalidateLists();
  const [globalFilter, setGlobalFilter] = useState("");

  const handleActionSuccess = useCallback(() => {
    invalidate("listItemVersions");
  }, [invalidate]);

  const entries = items.data?.entries ?? [];
  const columns = getItemColumns(handleActionSuccess);
  const table = useReactTable({
    data: entries,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (items.isLoading) {
    return <div className="text-muted-foreground">Loading...</div>;
  }

  if (items.error) {
    return <div className="text-destructive">{items.error}</div>;
  }

  if (entries.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <DataTable
      table={table}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      columnCount={columns.length}
      searchPlaceholder="Search items..."
      emptyMessage="No items."
      permanentFilter={search || undefined}
    />
  );
}

interface PostTableProps {
  search?: string;
  emptyState?: ReactNode;
}

export function PostTable({ search = "", emptyState }: PostTableProps) {
  const posts = useListPosts(search);
  const invalidate = useInvalidateLists();
  const [globalFilter, setGlobalFilter] = useState("");

  const handleActionSuccess = useCallback(() => {
    invalidate("listPosts");
  }, [invalidate]);

  const entries = posts.data?.entries ?? [];
  const columns = getPostColumns(handleActionSuccess);
  const table = useReactTable({
    data: entries,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (posts.isLoading) {
    return <div className="text-muted-foreground">Loading...</div>;
  }

  if (posts.error) {
    return <div className="text-destructive">{posts.error}</div>;
  }

  if (entries.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <DataTable
      table={table}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      columnCount={columns.length}
      searchPlaceholder="Search posts..."
      emptyMessage="No posts."
      permanentFilter={search || undefined}
    />
  );
}

interface IntegrationTableProps {
  search?: string;
  emptyState?: ReactNode;
}

export function IntegrationTable({
  search = "",
  emptyState,
}: IntegrationTableProps) {
  const integrations = useListIntegrations(search);
  const invalidate = useInvalidateLists();
  const [globalFilter, setGlobalFilter] = useState("");

  const handleActionSuccess = useCallback(() => {
    invalidate("listIntegrations");
  }, [invalidate]);

  const entries = integrations.data?.entries ?? [];
  const columns = getIntegrationsColumns(handleActionSuccess);
  const table = useReactTable({
    data: entries,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (integrations.isLoading) {
    return <div className="text-muted-foreground">Loading...</div>;
  }

  if (integrations.error) {
    return <div className="text-destructive">{integrations.error}</div>;
  }

  if (entries.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <DataTable
      table={table}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      columnCount={columns.length}
      searchPlaceholder="Search integrations..."
      emptyMessage="No integrations."
      permanentFilter={search || undefined}
    />
  );
}

function getActionColumns(
  users: Map<string, OrgUser>,
): ColumnDef<ActionLogEntry>[] {
  return [
    {
      accessorKey: "success",
      header: "Status",
      cell: ({ row }) =>
        row.original.success ? (
          <CheckCircle className="size-4 text-green-600" />
        ) : (
          <XCircle className="size-4 text-red-600" />
        ),
    },
    {
      accessorKey: "actor",
      header: "Actor",
      cell: ({ row }) => (
        <Actor actor={row.original.actor} users={users} showName={false} />
      ),
    },
    {
      accessorKey: "verb",
      header: "Verb",
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.verb}</span>
      ),
    },
    {
      accessorKey: "params",
      header: "Params",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {JSON.stringify(row.original.params)}
        </span>
      ),
    },
    {
      accessorKey: "createdAt",
      header: "Time",
      cell: ({ row }) => (
        <DateDisplay value={row.original.createdAt} withTime={true} />
      ),
    },
  ];
}

interface ActionTableProps {
  search?: string;
  emptyState?: ReactNode;
}

export function ActionTable({ search = "", emptyState }: ActionTableProps) {
  const actionsResult = useListActions(search);
  const usersResult = useListUsers();
  const [globalFilter, setGlobalFilter] = useState("");

  const users = useMemo(
    () => new Map((usersResult.data?.entries ?? []).map(u => [u.id, u])),
    [usersResult.data],
  );

  const loading = actionsResult.isLoading || usersResult.isLoading;
  const error = actionsResult.error ?? usersResult.error ?? null;
  const entries = actionsResult.data?.entries ?? [];

  const columns = getActionColumns(users);
  const table = useReactTable({
    data: entries,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (loading) {
    return <div className="text-muted-foreground">Loading...</div>;
  }

  if (error) {
    return <div className="text-destructive">{error}</div>;
  }

  if (entries.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <DataTable
      table={table}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      columnCount={columns.length}
      searchPlaceholder="Search actions..."
      emptyMessage="No actions."
      permanentFilter={search || undefined}
    />
  );
}
